import React, { useEffect, useState } from 'react';
import { Volume2 } from 'lucide-react';
import RecordedAudioPlayer from './RecordedAudioPlayer';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { useVoiceCloningStore } from '../../../zustand-store/useVoiceCloningStore';
import { useClonedVoicePreview } from '../../../http-hooks/ai-voices/useClonedVoice';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';


// The 30 languages the clone engine supports, A to Z. The website's voice cloning tool has the same
// list with the same codes; keep the two in step. Fixed on purpose: the engine only uses the 2-letter
// prefix, and filtering by the voices catalogue hid Norwegian (nb-NO) and Tagalog (fil-PH).
const CLONE_LANGUAGES: { code: string; name: string }[] = [
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'my-MM', name: 'Burmese' },
    { code: 'zh-CN', name: 'Chinese' },
    { code: 'da-DK', name: 'Danish' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'en-US', name: 'English' },
    { code: 'fi-FI', name: 'Finnish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'el-GR', name: 'Greek' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'id-ID', name: 'Indonesian' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'km-KH', name: 'Khmer' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'lo-LA', name: 'Lao' },
    { code: 'ms-MY', name: 'Malay' },
    { code: 'no-NO', name: 'Norwegian' },
    { code: 'pl-PL', name: 'Polish' },
    { code: 'pt-BR', name: 'Portuguese' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'sw-KE', name: 'Swahili' },
    { code: 'sv-SE', name: 'Swedish' },
    { code: 'tl-PH', name: 'Tagalog' },
    { code: 'th-TH', name: 'Thai' },
    { code: 'tr-TR', name: 'Turkish' },
    { code: 'vi-VN', name: 'Vietnamese' },
];
const DEFAULT_CLONE_LANGUAGE = 'en-US';

const inputStyle: React.CSSProperties = {
    fontSize: '14px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid rgba(var(--bs-primary-rgb), 0.18)',
    background: 'rgba(255,255,255,0.80)',
    color: '#1a1f36',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.18s ease',
};

const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#4a5568',
    marginBottom: 8,
    display: 'block',
};

const VoiceDetailsStep: React.FC = () => {
    const { org_id, id: integration_id } = useParams<{ org_id: string; id: string }>();
    const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);

    const { audioFile, voiceDetails, uploadedAudioUrl, setVoiceDetails } = useVoiceCloningStore((state) => ({
        audioFile: state.audioFile,
        voiceDetails: state.voiceDetails,
        uploadedAudioUrl: state.uploadedAudioUrl,
        setVoiceDetails: state.setVoiceDetails
    }));

    
    const [localAudioUrl, setLocalAudioUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!audioFile) { setLocalAudioUrl(null); return; }
        const url = URL.createObjectURL(audioFile);
        setLocalAudioUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [audioFile]);

    const previewMutation = useClonedVoicePreview();

    useEffect(() => {
        if (!voiceDetails.languageCode) setVoiceDetails({ languageCode: DEFAULT_CLONE_LANGUAGE });
    }, [voiceDetails.languageCode, setVoiceDetails]);

    const handlePreview = async () => {
        if (!uploadedAudioUrl || !voiceDetails.languageCode) {
            toast.error('Please ensure voice sample is uploaded and language is selected');
            return;
        }
        try {
            const previewData = await previewMutation.mutateAsync({
                org_id: org_id!,
                integration_id: integration_id!,
                audio_sample_url: uploadedAudioUrl,
                language_code: voiceDetails.languageCode
            });
            setPreviewAudioUrl(previewData.audioUrl);
            toast.success('Preview generated successfully!');
        } catch (error: any) {
            console.error('Preview generation error:', error);
            toast.error(error.message || 'Failed to generate preview');
        }
    };

    return (
        <div style={{ maxWidth: '600px' }}>
            {/* Voice Sample Player */}
            {localAudioUrl && audioFile && (
                <div className="mb-4">
                    <label style={labelStyle}>Voice Sample</label>
                    <RecordedAudioPlayer audioUrl={localAudioUrl} fileName={audioFile.name} />
                </div>
            )}

            {/* Test Your Voice — hidden until preview flow is redesigned */}
            {false && <div className="mb-4 p-4" style={{
                background: 'rgba(var(--bs-primary-rgb), 0.04)',
                border: '1px solid rgba(var(--bs-primary-rgb), 0.14)',
                borderRadius: '14px',
            }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Volume2 size={16} color="var(--bs-primary)" />
                    <h6 style={{ fontSize: '14px', fontWeight: 600, color: '#1a1f36', margin: 0 }}>Test Your Voice</h6>
                </div>
                <p style={{ fontSize: '12px', color: '#90a0b7', marginBottom: 16 }}>
                    Generate a sample to hear how your cloned voice sounds with our test phrase.
                </p>

                <button
                    onClick={handlePreview}
                    disabled={!voiceDetails.languageCode || !uploadedAudioUrl || previewMutation.isPending}
                    style={{
                        background: (voiceDetails.languageCode && uploadedAudioUrl && !previewMutation.isPending)
                            ? 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)'
                            : 'rgba(var(--bs-primary-rgb), 0.08)',
                        color: (voiceDetails.languageCode && uploadedAudioUrl && !previewMutation.isPending) ? 'white' : '#90a0b7',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '9px 20px',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 7,
                        cursor: (voiceDetails.languageCode && uploadedAudioUrl) ? 'pointer' : 'not-allowed',
                        boxShadow: (voiceDetails.languageCode && uploadedAudioUrl) ? '0 4px 14px rgba(var(--bs-primary-rgb), 0.25)' : 'none',
                        transition: 'all 0.2s ease',
                    }}
                >
                    {previewMutation.isPending ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <Volume2 size={14} />
                            Generate Preview
                        </>
                    )}
                </button>

                {previewAudioUrl && (
                    <div style={{ marginTop: 16 }}>
                        <label style={{ ...labelStyle, marginBottom: 8 }}>Preview Result</label>
                        <RecordedAudioPlayer audioUrl={previewAudioUrl!} fileName="preview.mp3" />
                    </div>
                )}
            </div>}

            {/* Voice Name */}
            <div className="mb-4">
                <label style={labelStyle}>
                    Voice Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="text"
                    placeholder="Enter a name for this voice"
                    value={voiceDetails.name}
                    onChange={(e) => setVoiceDetails({ name: e.target.value })}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bs-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(var(--bs-primary-rgb), 0.10)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.18)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                <span style={{ fontSize: '11px', color: '#90a0b7', marginTop: 4, display: 'block' }}>Choose a memorable name to identify this voice</span>
            </div>

            {/* Language */}
            <div className="mb-4">
                <label style={labelStyle}>
                    Language <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <SearchableSelect
                    selected={voiceDetails.languageCode}
                    onChange={(code: string) => setVoiceDetails({ languageCode: code })}
                    options={CLONE_LANGUAGES.map((lang) => ({ value: lang.code, label: lang.name }))}
                    placeholder="Select language"
                />
                <span style={{ fontSize: '11px', color: '#90a0b7', marginTop: 4, display: 'block' }}>Select the language spoken in this voice sample</span>
            </div>

            {/* Description */}
            <div className="mb-4">
                <label style={labelStyle}>
                    Description <span style={{ fontSize: '12px', fontWeight: 400, color: '#90a0b7' }}>(Optional)</span>
                </label>
                <textarea
                    rows={3}
                    placeholder="Add a description for this voice (e.g., tone, characteristics, use cases)"
                    value={voiceDetails.description}
                    onChange={(e) => setVoiceDetails({ description: e.target.value })}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bs-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(var(--bs-primary-rgb), 0.10)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.18)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                <span style={{ fontSize: '11px', color: '#90a0b7', marginTop: 4, display: 'block' }}>
                    {voiceDetails.description.length}/500 characters
                </span>
            </div>

            {/* Rights Consent */}
            <div className="mb-4 p-4" style={{
                background: 'rgba(239, 68, 68, 0.04)',
                border: '1px solid rgba(239, 68, 68, 0.18)',
                borderRadius: '12px',
            }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        id="voice-rights-checkbox"
                        checked={voiceDetails.hasRights}
                        onChange={(e) => setVoiceDetails({ hasRights: e.target.checked })}
                        style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--bs-primary)', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>
                        I confirm that I have all necessary rights and permissions to use this voice,
                        and that it does not infringe on any third-party rights or contain copyrighted material.{' '}
                        <span style={{ color: '#ef4444' }}>*</span>
                    </span>
                </label>
            </div>
        </div>
    );
};

export default VoiceDetailsStep;
